import React, { useEffect, useMemo, useState } from 'react';

import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
} from '@mui/material';

import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';

import { useTranslation } from 'react-i18next';

import { productionMethodAPI } from '../../services/apiService';

import './ProduceDialog.css';

function ProduceDialog({ open, onClose, onProduced, inventories = [] }) {
    const { t, i18n } = useTranslation();

    const [productionMethods, setProductionMethods] = useState([]);

    const [selectedMethodId, setSelectedMethodId] = useState('');

    const [productionUnits, setProductionUnits] = useState('');

    const [selectedMethod, setSelectedMethod] = useState(null);

    const [loadingMethods, setLoadingMethods] = useState(false);

    const [producing, setProducing] = useState(false);

    const [error, setError] = useState('');

    useEffect(() => {
        if (!open) return;

        const fetchMethods = async () => {
            try {
                setLoadingMethods(true);
                setError('');

                const result = await productionMethodAPI.getAll({
                    force: true,
                    fallback: [],
                });

                setProductionMethods(Array.isArray(result) ? result : []);
            } catch (err) {
                console.error(err);

                setError(err.message || t('production_methods_fetch_error'));
            } finally {
                setLoadingMethods(false);
            }
        };

        fetchMethods();
    }, [open, t]);

    useEffect(() => {
        if (!open) return;

        setSelectedMethodId('');
        setProductionUnits('');
        setSelectedMethod(null);
        setError('');
    }, [open]);

    const handleMethodChange = async (event) => {
        const methodId = event.target.value;

        setSelectedMethodId(methodId);
        setProductionUnits('');
        setSelectedMethod(null);
        setError('');

        if (!methodId) return;

        try {
            setLoadingMethods(true);

            const method = await productionMethodAPI.getById(methodId, {
                force: true,
            });

            setSelectedMethod(method);
        } catch (err) {
            console.error(err);

            setError(err.message || t('production_formula_fetch_error'));
        } finally {
            setLoadingMethods(false);
        }
    };

    const preview = useMemo(() => {
        if (!selectedMethod || !productionUnits || Number(productionUnits) <= 0) {
            return [];
        }

        return (selectedMethod.ingredient_lines || []).map((line) => {
            const inventoryItem = inventories.find(
                (item) =>
                    Number(item.id) ===
                    Number(
                        typeof line.ingredient === 'object' ? line.ingredient?.id : line.ingredient
                    )
            );

            const required = Number(line.quantity) * Number(productionUnits);

            const available = Number(inventoryItem?.quantity || 0);

            return {
                ...line,
                inventoryItem,
                required,
                available,
                enough: available >= required,
            };
        });
    }, [selectedMethod, productionUnits, inventories]);

    const insufficientStock = preview.some((item) => !item.enough);

    const handleProduce = async () => {
        if (!selectedMethodId || !productionUnits || Number(productionUnits) <= 0) {
            setError(t('production_invalid_amount'));

            return;
        }

        if (insufficientStock) {
            setError(t('insufficient_inventory_for_production'));

            return;
        }

        try {
            setProducing(true);
            setError('');

            await productionMethodAPI.produce(selectedMethodId, Number(productionUnits));

            if (onProduced) {
                await onProduced();
            }

            onClose();
        } catch (err) {
            console.error(err);

            setError(err.message || t('production_error'));
        } finally {
            setProducing(false);
        }
    };

    const handleClose = () => {
        if (producing) return;

        onClose();
    };

    const formatNumber = (value) => {
        if (value === null || value === undefined || value === '') {
            return '-';
        }

        const number = Number(value);

        if (!Number.isFinite(number)) {
            return value;
        }
        return new Intl.NumberFormat(i18n.language === 'fa' ? 'fa-IR' : 'en-US', {
            maximumFractionDigits: 4,
        }).format(number);
    };

    const getUnitLabel = (unit) => {
        const unitKeys = {
            kg: 'unit_kg',
            g: 'unit_g',
            liter: 'unit_liter',
            ml: 'unit_ml',
            piece: 'unit_piece',
            pack: 'unit_pack',
        };
        return unitKeys[unit] ? t(unitKeys[unit]) : unit || '-';
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            className="produce_dialog"
        >
            <DialogTitle>
                <div className="produce_dialog_title">
                    <PrecisionManufacturingIcon />

                    <span>{t('produce')}</span>
                </div>
            </DialogTitle>

            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {productionMethods.length === 0 && !loadingMethods ? (
                    <div className="produce_no_formulations">
                        <PrecisionManufacturingIcon />

                        <h3>{t('no_formulations_available')}</h3>

                        <p>{t('create_formulation_first')}</p>
                    </div>
                ) : (
                    <div className="produce_form">
                        <FormControl fullWidth>
                            <InputLabel>{t('select_formulation')}</InputLabel>

                            <Select
                                value={selectedMethodId}
                                label={t('select_formulation')}
                                onChange={handleMethodChange}
                                disabled={loadingMethods || producing}
                            >
                                {productionMethods.map((method) => (
                                    <MenuItem key={method.id} value={method.id}>
                                        <div className="produce_method_option">
                                            <strong>{method.name}</strong>

                                            <span>{method.output_name}</span>
                                        </div>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {selectedMethod && (
                            <>
                                <div className="produce_output_card">
                                    <div>
                                        <span>{t('production_output')}</span>

                                        <strong>{selectedMethod.output_name}</strong>
                                    </div>

                                    <div>
                                        <span>{t('standard_amount')}</span>

                                        <strong>
                                            {formatNumber(selectedMethod.output_unit_value)}{' '}
                                            {getUnitLabel(selectedMethod.output_unit)}
                                        </strong>
                                    </div>
                                </div>

                                <TextField
                                    label={t('production_amount')}
                                    type="number"
                                    value={productionUnits}
                                    onChange={(event) => setProductionUnits(event.target.value)}
                                    fullWidth
                                    disabled={producing}
                                    inputProps={{
                                        min: 0.0001,
                                        step: 'any',
                                    }}
                                />

                                {productionUnits && Number(productionUnits) > 0 && (
                                    <div className="produce_preview">
                                        <h3>{t('required_ingredients')}</h3>

                                        {preview.map((item) => (
                                            <div
                                                key={item.id}
                                                className={`produce_preview_row ${
                                                    item.enough ? 'enough' : 'insufficient'
                                                }`}
                                            >
                                                <div className="produce_ingredient_info">
                                                    <strong>{item.ingredient_name}</strong>

                                                    <span>
                                                        {t('available')}:
                                                        {formatNumber(item.available)}{' '}
                                                        {getUnitLabel(item.inventoryItem?.unit)}
                                                    </span>
                                                </div>

                                                <div className="produce_required_info">
                                                    <strong>
                                                        {formatNumber(item.required)}{' '}
                                                        {getUnitLabel(item.inventoryItem?.unit)}
                                                    </strong>

                                                    <span
                                                        className={
                                                            item.enough ? 'stock_ok' : 'stock_bad'
                                                        }
                                                    >
                                                        {item.enough ? '✓' : '✕'}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}

                                        {insufficientStock && (
                                            <Alert severity="warning">
                                                {t('insufficient_inventory_for_production')}
                                            </Alert>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose} disabled={producing}>
                    {t('cancel')}
                </Button>

                <Button
                    variant="contained"
                    color="success"
                    onClick={handleProduce}
                    disabled={
                        producing ||
                        loadingMethods ||
                        !selectedMethodId ||
                        !productionUnits ||
                        Number(productionUnits) <= 0 ||
                        insufficientStock
                    }
                >
                    {producing ? t('loading') : t('produce')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default ProduceDialog;
